import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Header } from '../../components/layout/Header'
import { Card } from '../../components/common/Card'
import { Button } from '../../components/common/Button'
import axios from 'axios'
import { useTheme } from '../../contexts/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

interface RegionData {
  region: string
  countries: string[]
  seller_count: number
  order_count: number
  total_revenue: number
  avg_order_value: number
  growth_rate: number
  top_products: string[]
}

export const GeographicPage: React.FC = () => {
  const { colors } = useTheme()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['analytics-geographic'],
    queryFn: async () => {
      const { data } = await axios.get(`${API_BASE_URL}/markets/analytics/geographic`)
      return data
    },
  })

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #E0E0E0',
              borderTop: '4px solid #4A90E2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            <p style={{ color: colors.textSecondary }}>Loading geographic analytics...</p>
          </div>
        </main>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
        <Header />
        <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
          <Card style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: '#EF4444' }}>Error loading data: {(error as Error).message}</p>
          </Card>
        </main>
      </div>
    )
  }

  const regions: RegionData[] = data?.regions || []
  const countryBreakdown = data?.country_breakdown || []

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      <Header />
      <main style={{ maxWidth: '1440px', margin: '0 auto', padding: '40px 32px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '8px', color: colors.textPrimary }}>
            Geographic Analysis
          </h1>
          <p style={{ color: colors.textSecondary, fontSize: '16px' }}>
            Analyze market performance across different geographic regions
          </p>
        </div>

        {/* Regions Overview */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '32px' }}>
          {regions.map((region) => (
            <Card key={region.region} style={{ padding: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>{region.region}</h3>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '20px',
                  backgroundColor: region.growth_rate >= 0 ? '#50C87820' : '#EF444420',
                  color: region.growth_rate >= 0 ? '#50C878' : '#EF4444',
                  fontWeight: 600,
                  fontSize: '13px',
                }}>
                  {region.growth_rate >= 0 ? '+' : ''}{region.growth_rate.toFixed(1)}%
                </span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '4px' }}>Total Revenue</p>
                <p style={{ fontSize: '28px', fontWeight: 700, color: colors.primary }}>
                  ${region.total_revenue.toLocaleString()}
                </p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <p style={{ color: colors.textSecondary, fontSize: '13px' }}>Sellers</p>
                  <p style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{region.seller_count}</p>
                </div>
                <div>
                  <p style={{ color: colors.textSecondary, fontSize: '13px' }}>Orders</p>
                  <p style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>{region.order_count.toLocaleString()}</p>
                </div>
              </div>

              <div>
                <p style={{ color: colors.textSecondary, fontSize: '13px', marginBottom: '8px' }}>Countries</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {region.countries.slice(0, 5).map(country => (
                    <span
                      key={country}
                      onClick={() => navigate(`/markets/${country}`)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: colors.background,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: colors.textPrimary,
                        cursor: 'pointer',
                      }}
                    >
                      {country}
                    </span>
                  ))}
                  {region.countries.length > 5 && (
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: colors.primary + '10',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: colors.primary,
                    }}>+{region.countries.length - 5} more</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Country Breakdown Table */}
        <Card>
          <h2 style={{ fontSize: '20px', marginBottom: '20px', color: colors.textPrimary }}>Country Breakdown</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Country</th>
                  <th style={{ textAlign: 'left', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Region</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Sellers</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Orders</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Revenue</th>
                  <th style={{ textAlign: 'right', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>AOV</th>
                  <th style={{ textAlign: 'center', padding: '12px', color: colors.textSecondary, fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {countryBreakdown.map((country: {
                  country: string
                  region: string
                  seller_count: number
                  order_count: number
                  revenue: number
                  avg_order_value: number
                }, idx: number) => (
                  <tr key={country.country} style={{
                    borderBottom: `1px solid ${colors.border}`,
                    backgroundColor: idx % 2 === 0 ? 'transparent' : colors.background,
                  }}>
                    <td style={{ padding: '14px 12px', fontWeight: 500, color: colors.textPrimary }}>{country.country}</td>
                    <td style={{ padding: '14px 12px', color: colors.textSecondary }}>{country.region}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>{country.seller_count}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>{country.order_count.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${country.revenue.toLocaleString()}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'right', color: colors.textPrimary }}>${country.avg_order_value.toFixed(2)}</td>
                    <td style={{ padding: '14px 12px', textAlign: 'center' }}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/markets/${country.country}`)}
                      >
                        View Market
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </main>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
